import React, { useState } from 'react';
import { Box, TextField, Typography, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import { CustomColumnDefinition } from '../../types/lead';
import { DynamicFieldInput } from './DynamicFieldInput';
import { getCustomFieldValue, resolveOptionLabel } from '../../utils/leads/leadsUtils';

interface DynamicFormFieldsProps {
  customColumns?: CustomColumnDefinition[];
  notes?: string;
  customFields?: Record<string, any>;
  onNotesChange?: (notes: string) => void;
  onCustomFieldChange?: (key: string, value: any) => void;
  onAddOption?: (columnKey: string, optionLabel: string) => Promise<any>;
  errors?: Record<string, string>;
  defaultExpanded?: boolean;
  mode?: 'conflict' | 'edit' | 'view';
  existingNotes?: string;
  existingCustomFields?: Record<string, any>;
}

const formatCustomFieldValueForDisplay = (val: any, type?: string, options?: string[]): string | null => {
  if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
    return null;
  }
  if (type === 'multi_select' || Array.isArray(val)) {
    let arr: any[] = [];
    if (Array.isArray(val)) {
      arr = val;
    } else if (typeof val === 'string' && val.trim() !== '') {
      const trimmed = val.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) arr = parsed;
          else arr = [parsed];
        } catch (e) {
          arr = trimmed.split(',').map((s) => s.trim());
        }
      } else {
        arr = trimmed.split(',').map((s) => s.trim());
      }
    } else {
      arr = [val];
    }
    const resolved = arr.map((item) => resolveOptionLabel(item, options));
    return resolved.filter(Boolean).join(', ');
  }
  if (type === 'select') {
    return resolveOptionLabel(val, options);
  }
  if (typeof val === 'boolean' || type === 'boolean') {
    return val === true || val === 'true' ? 'Yes' : 'No';
  }
  if (type === 'date' && (typeof val === 'string' || typeof val === 'number')) {
    if (typeof val === 'string' && val.includes('-') && !val.includes('T')) {
      const parts = val.split('-');
      if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1;
        const d = parseInt(parts[2], 10);
        const dateObj = new Date(y, m, d);
        if (!isNaN(dateObj.getTime())) {
          return format(dateObj, 'dd MMM yyyy');
        }
      }
    }
    const dateObj = new Date(val);
    if (!isNaN(dateObj.getTime())) {
      return format(dateObj, 'dd MMM yyyy');
    }
  }
  if (type === 'datetime' && (typeof val === 'string' || typeof val === 'number')) {
    const dateObj = new Date(val);
    if (!isNaN(dateObj.getTime())) {
      return format(dateObj, 'dd MMM yyyy, hh:mm a');
    }
  }
  if (type === 'time' && typeof val === 'string') {
    const parts = val.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      if (!isNaN(hours)) {
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const h12 = hours % 12 || 12;
        return `${h12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
      }
    }
  }
  return String(val);
};

export const DynamicFormFields: React.FC<DynamicFormFieldsProps> = ({
  customColumns = [],
  notes = '',
  customFields = {},
  onNotesChange,
  onCustomFieldChange,
  onAddOption,
  errors = {},
  defaultExpanded = false,
  mode,
  existingNotes,
  existingCustomFields,
}) => {
  const activeColumns = customColumns.filter((col) => col.is_active !== false);

  // Auto expand if defaultExpanded is true, or there are field errors, or mode === 'conflict'
  const hasErrors = activeColumns.some((col) => !!errors[col.key]);

  const [expanded, setExpanded] = useState<boolean>(
    defaultExpanded || hasErrors || mode === 'conflict'
  );

  let notesHelperText = errors.notes;
  if (!notesHelperText && mode === 'conflict' && existingNotes?.trim()) {
    notesHelperText = `Currently saved as: ${existingNotes.trim()}`;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      {/* 1. Default Field: Notes */}
      {onNotesChange && (
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          label="Notes"
          placeholder="Add notes about this lead..."
          value={notes || ''}
          onChange={(e) => onNotesChange(e.target.value)}
          error={!!errors.notes}
          FormHelperTextProps={{
            sx: { wordBreak: 'break-word', overflowWrap: 'anywhere' }
          }}
          helperText={notesHelperText}
        />
      )}

      {/* 2. SMB Custom Fields inside Bordered Container */}
      {activeColumns.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 1.5,
          }}
        >
          <Box
            onClick={() => setExpanded(!expanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none',
              width: '100%',
              minHeight: 24,
            }}
          >
            <Typography
              variant="subtitle2"
              color={expanded ? 'primary.main' : 'text.secondary'}
              sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.2 }}
            >
              Additional Details
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
                p: 0.25,
                mr: -1,
                color: expanded ? 'primary.main' : 'text.secondary',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>

          <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                pt: 1.5,
              }}
            >
              {activeColumns.map((col) => {
                let colHelperText = errors[col.key];
                if (!colHelperText && mode === 'conflict') {
                  const rawVal = getCustomFieldValue(existingCustomFields, col.key);
                  const displayVal = formatCustomFieldValueForDisplay(rawVal, col.type, col.options);
                  if (displayVal !== null) {
                    colHelperText = `Currently saved as: ${displayVal}`;
                  }
                }

                return (
                  <DynamicFieldInput
                    key={col.key}
                    column={col}
                    value={getCustomFieldValue(customFields, col.key)}
                    onChange={(val) => onCustomFieldChange && onCustomFieldChange(col.key, val)}
                    onAddOption={onAddOption}
                    error={errors[col.key]}
                    helperText={colHelperText}
                  />
                );
              })}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
